/**
 * Freight Cities Reference Data Module
 * Contains city data with coordinates and freight rates for ROI calculations
 */

export interface FreightCity {
  city: string;
  region: string;
  country: string;
  currency: 'CAD' | 'USD';
  rate: number; // Rate per truck
  coordinates: {
    lat: number;
    lng: number;
  };
}

export interface FreightCitySearchResult {
  city: FreightCity;
  distance?: number; // Distance in km for fallback calculations
}

/**
 * Reference cities with freight rates and coordinates
 * Rates are per truck (16 pallets capacity)
 */
export const FREIGHT_CITIES: FreightCity[] = [
  // Canada - Major Cities
  {
    city: 'Vancouver',
    region: 'BC',
    country: 'Canada',
    currency: 'CAD',
    rate: 1550,
    coordinates: { lat: 49.2827, lng: -123.1207 }
  },
  {
    city: 'Calgary',
    region: 'AB',
    country: 'Canada',
    currency: 'CAD',
    rate: 2100,
    coordinates: { lat: 51.0447, lng: -114.0719 }
  },
  {
    city: 'Edmonton',
    region: 'AB',
    country: 'Canada',
    currency: 'CAD',
    rate: 2300,
    coordinates: { lat: 53.5461, lng: -113.4938 }
  },
  {
    city: 'Winnipeg',
    region: 'MB',
    country: 'Canada',
    currency: 'CAD',
    rate: 2800,
    coordinates: { lat: 49.8951, lng: -97.1384 }
  },
  {
    city: 'Toronto',
    region: 'ON',
    country: 'Canada',
    currency: 'CAD',
    rate: 3800,
    coordinates: { lat: 43.6532, lng: -79.3832 }
  },
  {
    city: 'Ottawa',
    region: 'ON',
    country: 'Canada',
    currency: 'CAD',
    rate: 3900,
    coordinates: { lat: 45.4215, lng: -75.6972 }
  },
  {
    city: 'Montreal',
    region: 'QC',
    country: 'Canada',
    currency: 'CAD',
    rate: 4100,
    coordinates: { lat: 45.5017, lng: -73.5673 }
  },
  {
    city: 'Quebec City',
    region: 'QC',
    country: 'Canada',
    currency: 'CAD',
    rate: 4300,
    coordinates: { lat: 46.8139, lng: -71.2080 }
  },
  {
    city: 'Halifax',
    region: 'NS',
    country: 'Canada',
    currency: 'CAD',
    rate: 5200,
    coordinates: { lat: 44.6488, lng: -63.5752 }
  },

  // USA - Major Cities
  {
    city: 'Seattle',
    region: 'WA',
    country: 'USA',
    currency: 'USD',
    rate: 1200,
    coordinates: { lat: 47.6062, lng: -122.3321 }
  },
  {
    city: 'Portland',
    region: 'OR',
    country: 'USA',
    currency: 'USD',
    rate: 1400,
    coordinates: { lat: 45.5152, lng: -122.6784 }
  },
  {
    city: 'San Francisco',
    region: 'CA',
    country: 'USA',
    currency: 'USD',
    rate: 1600,
    coordinates: { lat: 37.7749, lng: -122.4194 }
  },
  {
    city: 'Los Angeles',
    region: 'CA',
    country: 'USA',
    currency: 'USD',
    rate: 1800,
    coordinates: { lat: 34.0522, lng: -118.2437 }
  },
  {
    city: 'Denver',
    region: 'CO',
    country: 'USA',
    currency: 'USD',
    rate: 2200,
    coordinates: { lat: 39.7392, lng: -104.9903 }
  },
  {
    city: 'Phoenix',
    region: 'AZ',
    country: 'USA',
    currency: 'USD',
    rate: 2000,
    coordinates: { lat: 33.4484, lng: -112.0740 }
  },
  {
    city: 'Dallas',
    region: 'TX',
    country: 'USA',
    currency: 'USD',
    rate: 2800,
    coordinates: { lat: 32.7767, lng: -96.7970 }
  },
  {
    city: 'Houston',
    region: 'TX',
    country: 'USA',
    currency: 'USD',
    rate: 2900,
    coordinates: { lat: 29.7604, lng: -95.3698 }
  },
  {
    city: 'Chicago',
    region: 'IL',
    country: 'USA',
    currency: 'USD',
    rate: 3200,
    coordinates: { lat: 41.8781, lng: -87.6298 }
  },
  {
    city: 'Detroit',
    region: 'MI',
    country: 'USA',
    currency: 'USD',
    rate: 3400,
    coordinates: { lat: 42.3314, lng: -83.0458 }
  },
  {
    city: 'Atlanta',
    region: 'GA',
    country: 'USA',
    currency: 'USD',
    rate: 3600,
    coordinates: { lat: 33.7490, lng: -84.3880 }
  },
  {
    city: 'New York',
    region: 'NY',
    country: 'USA',
    currency: 'USD',
    rate: 4200,
    coordinates: { lat: 40.7128, lng: -74.0060 }
  },
  {
    city: 'Boston',
    region: 'MA',
    country: 'USA',
    currency: 'USD',
    rate: 4400,
    coordinates: { lat: 42.3601, lng: -71.0589 }
  },
  {
    city: 'Miami',
    region: 'FL',
    country: 'USA',
    currency: 'USD',
    rate: 4800,
    coordinates: { lat: 25.7617, lng: -80.1918 }
  },

  // Mexico - Major Cities
  {
    city: 'Tijuana',
    region: 'BC',
    country: 'Mexico',
    currency: 'USD',
    rate: 1900,
    coordinates: { lat: 32.5149, lng: -117.0382 }
  },
  {
    city: 'Mexico City',
    region: 'CDMX',
    country: 'Mexico',
    currency: 'USD',
    rate: 3500,
    coordinates: { lat: 19.4326, lng: -99.1332 }
  },
  {
    city: 'Guadalajara',
    region: 'JAL',
    country: 'Mexico',
    currency: 'USD',
    rate: 3200,
    coordinates: { lat: 20.6597, lng: -103.3496 }
  },
  {
    city: 'Monterrey',
    region: 'NL',
    country: 'Mexico',
    currency: 'USD',
    rate: 3000,
    coordinates: { lat: 25.6866, lng: -100.3161 }
  }
];

/**
 * Search for a freight city by name (case-insensitive)
 * Returns exact match if found, null otherwise
 */
export function findFreightCity(cityName: string): FreightCity | null {
  const searchTerm = cityName.toLowerCase().trim();
  
  return FREIGHT_CITIES.find(city => 
    city.city.toLowerCase() === searchTerm ||
    `${city.city.toLowerCase()}, ${city.region.toLowerCase()}` === searchTerm ||
    `${city.city.toLowerCase()}, ${city.country.toLowerCase()}` === searchTerm
  ) || null;
}

/**
 * Get all cities for a specific country
 */
export function getCitiesByCountry(country: string): FreightCity[] {
  return FREIGHT_CITIES.filter(city => 
    city.country.toLowerCase() === country.toLowerCase()
  );
}

/**
 * Get city names formatted for dropdown display
 */
export function getFormattedCityNames(): string[] {
  return FREIGHT_CITIES.map(city => 
    `${city.city}, ${city.region}, ${city.country}`
  ).sort();
}

/**
 * Calculate distance between two coordinates using Haversine formula
 * Returns distance in kilometers
 */
export function calculateDistance(
  lat1: number, 
  lng1: number, 
  lat2: number, 
  lng2: number
): number {
  const R = 6371; // Earth's radius in kilometers
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/**
 * Find nearest reference city and calculate prorated rate
 * Used when destination city is not in reference list
 */
export function findNearestCityWithRate(
  targetLat: number, 
  targetLng: number
): FreightCitySearchResult | null {
  if (FREIGHT_CITIES.length === 0) return null;

  let nearestCity = FREIGHT_CITIES[0];
  let minDistance = calculateDistance(
    targetLat, 
    targetLng, 
    nearestCity.coordinates.lat, 
    nearestCity.coordinates.lng
  );

  for (const city of FREIGHT_CITIES.slice(1)) {
    const distance = calculateDistance(
      targetLat, 
      targetLng, 
      city.coordinates.lat, 
      city.coordinates.lng
    );
    
    if (distance < minDistance) {
      minDistance = distance;
      nearestCity = city;
    }
  }

  return {
    city: nearestCity,
    distance: minDistance
  };
}

/**
 * Validate freight city data structure
 */
export function validateFreightCity(city: any): city is FreightCity {
  return (
    typeof city === 'object' &&
    city !== null &&
    typeof city.city === 'string' &&
    typeof city.region === 'string' &&
    typeof city.country === 'string' &&
    (city.currency === 'CAD' || city.currency === 'USD') &&
    typeof city.rate === 'number' &&
    city.rate > 0 &&
    typeof city.coordinates === 'object' &&
    city.coordinates !== null &&
    typeof city.coordinates.lat === 'number' &&
    typeof city.coordinates.lng === 'number' &&
    city.coordinates.lat >= -90 &&
    city.coordinates.lat <= 90 &&
    city.coordinates.lng >= -180 &&
    city.coordinates.lng <= 180
  );
}